import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CountriesDropdownType } from 'core/widgets/CountriesDropdown/enum'
import { Addresses } from 'core/form'
import { InputAdornment, skeletonClasses } from '@mui/material'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import Grid from '@mui/material/Grid'
import Input from '@cbgms/components/form/Material/Input'
import { isRequired, isValidEmail, useVATFieldValidation } from '@cbgms/base/utils/validations'
import { normalizeUppercase } from '@cbgms/base/utils/normalizers'
import { formValueSelector, isSubmitting, reduxForm } from 'redux-form'
import useGetShopContactInformation from 'app/auth/components/hooks/use-get-shop-contact-information'
import { ValidatedWholesalerCredentials } from '../OnboardingWholesalerCredentialsStep/types.d'
import { OnboardingContactInformationSchema } from '@cbgms/api'
import { useSelector } from 'react-redux'
import { OnboardingState } from 'app/auth/components/hooks/use-onboarding-setup'
import { BaseStepLayout } from '../BaseStepLayout'
import { StepNavigation } from '../StepNavigation'
import { defaultStepFormProps } from '../types.d'
import HelpIcon from '@mui/icons-material/Help'
import Tooltip from '@mui/material/Tooltip'
import { WithRunInBackgroundHelper } from '../use-should-run-in-background'
import { useContactValidatorBuilder } from 'app/auth/components/hooks/use-contact-validator-builder'
import { generateSubmissionError } from 'utils/Form/SubmissionError'
import Dropdown from '@cbgms/components/form/Material/Dropdown'
import { useGetOnboardingRegions } from './use-onboarding-regions'
import { PhoneNumberInputRedux } from '@cbgms/components/form/PhoneNumberInput'

interface RFProp {
  shouldRunInBackground: boolean
}

const nonEmptyValues = (input: object): object => {
  const entriesWithActualValues = Object.entries(input).filter(([_, value]) => !!value)
  return Object.fromEntries(entriesWithActualValues)
}

const ReduxForm = reduxForm<OnboardingContactInformationSchema, RFProp>({
  form: 'contactInformationForm'
})(({ children, handleSubmit, shouldRunInBackground, submitting }) => (
  <WithRunInBackgroundHelper onSubmit={e => handleSubmit(e as any)} enabled={shouldRunInBackground} isSubmitting={submitting}>
    <form onSubmit={handleSubmit}>{children}</form>
  </WithRunInBackgroundHelper>
))

const ContactInformationForm: React.FC<
  defaultStepFormProps<OnboardingContactInformationSchema> & {
    basePath: string
    validatedVATNumber: string | null
    wholesalerCredentials: ValidatedWholesalerCredentials | null
    onSubmit: (data: OnboardingContactInformationSchema) => void
    onboardingInfo: OnboardingState
  }
> = ({
  validatedVATNumber,
  wholesalerCredentials,
  onSubmit,
  basePath,
  onboardingInfo,
  prevStep,
  isFirstStep,
  isPersisting,
  onSubmitSuccess,
  hideSkip,
  shouldRunInBackground,
  initialData,
  duplicationCheck
}) => {
  const form = 'contactInformationForm'
  const { t } = useTranslation()
  const country = useSelector(state => formValueSelector(form)(state, 'VisitorCountry'))
  const isSubmittingState = useSelector(state => isSubmitting(form)(state))
  const { isLoading, data: contactDetailsPrefilledValues } = useGetShopContactInformation(onboardingInfo, wholesalerCredentials!)
  const prefilledPhoneNumber =
    contactDetailsPrefilledValues?.PhoneNumber && contactDetailsPrefilledValues.PhoneNumber.length > 8
      ? contactDetailsPrefilledValues.PhoneNumber
      : ''
  const onboardingRegions = useGetOnboardingRegions()
  const initialFormValues = useMemo(
    () => ({
      ...contactDetailsPrefilledValues,
      PhoneNumber: prefilledPhoneNumber,
      RegionUUID: onboardingRegions.data?.length === 1 ? onboardingRegions.data[0].value : undefined,
      ...nonEmptyValues(initialData),
      VatIdentification: validatedVATNumber || contactDetailsPrefilledValues?.VatIdentification
    }),
    [validatedVATNumber, contactDetailsPrefilledValues, initialData, onboardingRegions.data]
  )

  const vatValidation = useVATFieldValidation(country || 'GB')
  const validators = useContactValidatorBuilder(basePath, onboardingInfo.onboardingMode, duplicationCheck)

  if (isLoading) {
    return <LoadingFormSkeleton />
  }
  return (
    <BaseStepLayout
      title={t('onboarding.contact_information.contact_information_header')}
      subTitles={[t('onboarding.contact_information.contact_information_step_info')]}
    >
      <Grid container>
        <ReduxForm
          initialValues={initialFormValues}
          onSubmitSuccess={onSubmitSuccess}
          onSubmit={async (formValues: OnboardingContactInformationSchema) => {
            // https://github.com/redux-form/redux-form/issues/4344
            // It seems where are facing an issue with Redux-form where initial values are undefined at the first render,
            // causing problems when you submit in the background.
            // To address this, we are taking initial values directly from props as a workaround.
            const values = shouldRunInBackground ? initialFormValues : formValues
            values.MGID = wholesalerCredentials?.Credentials.ExternalID || ''
            try {
              await validators(values as OnboardingContactInformationSchema)
              onSubmit(values as OnboardingContactInformationSchema)
            } catch (e) {
              throw generateSubmissionError(e)
            }
            return values
          }}
          shouldRunInBackground={shouldRunInBackground}
        >
          <Box>
            <Box>
              <Typography variant='h5' gutterBottom>
                {t('Account.ContactDetails')}
              </Typography>
              <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={6}>
                  <Input
                    fullWidth
                    label={t('setting_my_shop.shopname')}
                    inputProps={{ maxLength: 50 }}
                    name='Name'
                    required
                    validate={isRequired}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Input
                    fullWidth
                    label={t('setting_my_shop.vatidentification')}
                    normalize={normalizeUppercase}
                    inputProps={{ maxLength: vatValidation.maxLength }}
                    name='VatIdentification'
                    validate={vatValidation.validator}
                  />
                </Grid>
                <Grid item xs={6}>
                  <PhoneNumberInputRedux
                    defaultCountry={country}
                    labelTranslationKey='setting_my_shop.telephonenumber'
                    name='PhoneNumber'
                    required
                    validate={isRequired}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Input
                    fullWidth
                    label={t('setting_my_shop.emailaddress')}
                    InputProps={{
                      inputProps: { maxLength: 100 },
                      endAdornment: (
                        <InputAdornment position='end'>
                          <Tooltip title={`${t('onboarding.contact_information.contact_information_emailaddress_tooltip')}`}>
                            <HelpIcon />
                          </Tooltip>
                        </InputAdornment>
                      )
                    }}
                    name='EmailAddress'
                    required
                    validate={isValidEmail}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Dropdown fullWidth name='RegionUUID' label={t('setting_my_shop.region')} options={onboardingRegions.data ?? []} />
                </Grid>
              </Grid>
              <Addresses form={form} countries={CountriesDropdownType.onlyEnvironment} validate visitorAddressCountrySelectionDisabled />
            </Box>
          </Box>
          <StepNavigation prevStep={prevStep} hideBack={isFirstStep} isLoading={isSubmittingState || isPersisting} hideSkip={hideSkip} />
        </ReduxForm>
      </Grid>
    </BaseStepLayout>
  )
}

export default ContactInformationForm

const LoadingFormSkeleton: React.FC = () => {
  const titleHeight = 60
  const titleWidth = 200
  const inputHeight = 50

  return (
    <Box
      sx={{
        minWidth: theme => `calc(60vw - ${theme.spacing(2)})`,
        [`& .${skeletonClasses.text}`]: {
          margin: theme => theme.spacing(0.5)
        }
      }}
    >
      <Skeleton height={titleHeight} width={titleWidth} />
      <Grid container>
        <Grid item xs={6}>
          <Skeleton height={inputHeight} />
        </Grid>
        <Grid item xs={6}>
          <Skeleton height={inputHeight} />
        </Grid>
        <Grid item xs={6}>
          <Skeleton height={inputHeight} />
        </Grid>
        <Grid item xs={6}>
          <Skeleton height={inputHeight} />
        </Grid>
      </Grid>
      <Skeleton height={titleHeight} width={titleWidth} />
      <Grid container>
        <Grid item xs={4}>
          <Skeleton height={inputHeight} />
        </Grid>
        <Grid item xs={8}>
          <Skeleton height={inputHeight} />
        </Grid>
        <Grid item xs={4}>
          <Skeleton height={inputHeight} />
        </Grid>
        <Grid item xs={4}>
          <Skeleton height={inputHeight} />
        </Grid>
        <Grid item xs={4}>
          <Skeleton height={inputHeight} />
        </Grid>
        <Grid item xs={4}>
          <Skeleton height={inputHeight} />
        </Grid>
        <Grid item xs={4}>
          <Skeleton height={inputHeight} />
        </Grid>
        <Grid item xs={4}>
          <Skeleton height={inputHeight} />
        </Grid>
        <Grid item xs={4}>
          <Skeleton height={inputHeight} />
        </Grid>
      </Grid>
    </Box>
  )
}
